import { Chip, Paper, TextField, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Autocomplete from "@mui/material/Autocomplete";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";

export function TagsContainer({ contact }) {
    const theme = useTheme();
    const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Detecção adicional de mobile usando window.innerWidth para garantir
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 600; // sm breakpoint geralmente é 600px
        }
        return false;
    });
    
    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const [showAllTagsModal, setShowAllTagsModal] = useState(false);
    const tagsWrapperRef = useRef(null);
    const isMounted = useRef(true);
    
    const MAX_VISIBLE_TAGS_MOBILE = 3;
    const [maxVisibleTagsDesktop, setMaxVisibleTagsDesktop] = useState(6);
    const [forceRender, setForceRender] = useState(0);
    
    // Atualizar isMobile quando a janela redimensionar ou quando o breakpoint mudar
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 600 || isMobileBreakpoint;
            setIsMobile(mobile);
            // Forçar re-render quando mobile mudar
            setForceRender(prev => prev + 1);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [isMobileBreakpoint]);

    useEffect(() => {
        const calculateDesktopVisibleTags = () => {
            if (!tagsWrapperRef.current) return;
            const width = tagsWrapperRef.current.clientWidth || 0;
            // Reserva espaço para o input de busca e o chip "+X..."
            const availableForTags = Math.max(80, width - 200);

            const selectedTags = Array.isArray(selecteds) ? selecteds : [];
            if (selectedTags.length === 0) {
                setMaxVisibleTagsDesktop(Math.max(1, Math.floor(availableForTags / 110)));
                return;
            }

            // Mede pela largura real estimada do texto de cada tag (1 linha).
            let used = 0;
            let count = 0;
            for (const tag of selectedTags) {
                const name = typeof tag === "string" ? tag : (tag?.name || "");
                // ~7px por caractere + padding/close do Chip
                const chipWidth = Math.min(180, Math.max(56, name.length * 7 + 36));
                if (used + chipWidth > availableForTags) break;
                used += chipWidth;
                count += 1;
            }
            setMaxVisibleTagsDesktop(Math.max(1, count || 1));
        };

        calculateDesktopVisibleTags();
        window.addEventListener("resize", calculateDesktopVisibleTags);

        // O drawer/modal de contato altera a largura sem disparar resize da janela.
        let observer;
        if (typeof ResizeObserver !== "undefined" && tagsWrapperRef.current) {
            observer = new ResizeObserver(() => {
                calculateDesktopVisibleTags();
            });
            observer.observe(tagsWrapperRef.current);
        }

        return () => {
            window.removeEventListener("resize", calculateDesktopVisibleTags);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [selecteds]);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    // Carregar tags sempre que o componente montar
    useEffect(() => {
        if (isMounted.current) {
            loadTags();
        }
    }, []);

    const contactTagsFingerprint = useMemo(() => {
        if (!Array.isArray(contact?.tags)) return "";
        return contact.tags
            .map((tag) => tag?.id)
            .filter((id) => id != null)
            .sort((a, b) => a - b)
            .join(",");
    }, [contact?.tags]);

    // Carregar tags completas do contato (evita perda por join do Sequelize no ticket)
    useEffect(() => {
        let cancelled = false;

        const applyDisplayTags = (contactTags) => {
            if (!cancelled && isMounted.current) {
                setSelecteds(Array.isArray(contactTags) ? contactTags : []);
            }
        };

        if (!contact?.id) {
            const contactTags = Array.isArray(contact?.tags) ? contact.tags : [];
            applyDisplayTags(contactTags);
            return () => {
                cancelled = true;
            };
        }

        const loadContactTags = async () => {
            try {
                const { data } = await api.get(`/contacts/${contact.id}`);
                applyDisplayTags(Array.isArray(data?.tags) ? data.tags : []);
            } catch (err) {
                console.error('[TagsContainer] Erro ao carregar tags do contato:', err);
                const contactTags = Array.isArray(contact?.tags) ? contact.tags : [];
                applyDisplayTags(contactTags);
            }
        };

        loadContactTags();

        return () => {
            cancelled = true;
        };
    }, [contact?.id, contactTagsFingerprint]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, {
                params: { kanban: 0 }
            });
            if (isMounted.current) {
                const tagsArray = Array.isArray(data) ? data : [];
                setTags(tagsArray);
            }
        } catch (err) {
            console.error('[TagsContainer] Erro ao carregar tags:', err);
            if (isMounted.current) {
                setTags([]);
            }
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        // Se o contato não tem ID (novo contato), apenas atualizar o estado local
        if (!contact || !contact.id) {
            let optionsChanged = []
            if (reason === 'create-option') {
                if (isArray(value)) {
                    for (let item of value) {
                        if (item.length < 3) {
                            toastError("Tag muito curta!");
                            return;
                        }
                        if (isString(item)) {
                            // Criar a tag no backend mesmo sem contato
                            const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                            if (newTag) {
                                optionsChanged.push(newTag);
                            }
                        } else {
                            optionsChanged.push(item);
                        }
                    }
                }
                await loadTags();
            } else {
                optionsChanged = value;
            }
            
            if (isMounted.current) {
                setSelecteds(optionsChanged);
            }
            
            // Chamar callback se fornecido (para novos contatos)
            if (contact?.onTagsChange && typeof contact.onTagsChange === 'function') {
                contact.onTagsChange(optionsChanged);
            }
            
            return;
        }

        // Contato existente - sincronizar com backend
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        if (newTag) {
                            optionsChanged.push(newTag);
                        }
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        
        if (isMounted.current) {
            setSelecteds(optionsChanged);
        }
        
        try {
            await syncTags({ contactId: contact.id, tags: optionsChanged });
        } catch (err) {
            console.error('[TagsContainer] Erro ao sincronizar tags:', err);
            toastError(err);
        }
    }

    function getRandomHexColor() {
        // Gerar valores aleatórios para os componentes de cor
        const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255
      
        // Converter os componentes de cor em uma cor hexadecimal
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      
        return hexColor;
    }

    const maxVisibleTags = isMobile ? MAX_VISIBLE_TAGS_MOBILE : maxVisibleTagsDesktop;
    const shouldShowMoreIndicator = (selecteds || []).length > maxVisibleTags;
    const remainingTagsCount = shouldShowMoreIndicator ? (selecteds || []).length - maxVisibleTags : 0;

    return (
        <Paper style={{ 
            padding: '6px 16px', 
            minHeight: 'auto',
            display: 'flex',
            alignItems: 'center',
            height: '44px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div
                ref={tagsWrapperRef}
                style={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                flex: 1,
                flexWrap: 'nowrap'
            }}>
            <Autocomplete
                key={`autocomplete-${isMobile ? 'mobile' : 'desktop'}-${selecteds?.length || 0}-${forceRender || 0}-${maxVisibleTags}`}
                multiple
                size="small"
                style={{ 
                    width: shouldShowMoreIndicator ? 'calc(100% - 60px)' : '100%', 
                    margin: 0,
                    flex: 1,
                    minWidth: 0
                }}
                options={tags || []}
                value={selecteds || []}
                freeSolo
                limitTags={maxVisibleTags}
                getLimitTagsText={() => ""}
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                    if (typeof option === 'string' && typeof value === 'string') {
                        return option === value;
                    }
                    if (typeof option === 'object' && typeof value === 'object' && option && value) {
                        return option?.id === value?.id;
                    }
                    return false;
                }}
                renderOption={(props, option) => {
                    const tagName = typeof option === 'string' ? option : (option?.name || '');
                    const tagId = typeof option === 'string' ? option : (option?.id || tagName);
                    return (
                        <li {...props} key={tagId}>
                            <span>{tagName}</span>
                        </li>
                    );
                }}
                filterOptions={(options, state) => {
                    const { inputValue } = state;
                    // Se não há input, mostrar todas as opções
                    if (!inputValue || inputValue.trim() === '') {
                        return options || [];
                    }
                    const searchValue = inputValue.toLowerCase().trim();
                    const filtered = (options || []).filter((option) => {
                        const name = typeof option === 'string' ? option : (option?.name || '');
                        return name.toLowerCase().includes(searchValue);
                    });
                    return filtered;
                }}
                noOptionsText="Nenhuma tag encontrada"
                renderTags={(value, getTagProps) => {
                    const tagsArray = Array.isArray(value) ? value : [];
                    const shouldLimit = tagsArray.length > maxVisibleTags;
                    const visibleTags = shouldLimit
                        ? tagsArray.slice(0, maxVisibleTags)
                        : tagsArray;

                    return visibleTags.map((option, index) => {
                        const tagName = typeof option === 'string' ? option : (option?.name || '');
                        const tagColor = typeof option === 'string' ? '#eee' : (option?.color || '#eee');
                        const tagProps = getTagProps({ index });

                        return (
                            <Chip
                                key={`tag-${index}`}
                                variant="outlined"
                                style={{
                                    backgroundColor: tagColor,
                                    color: "#FFF",
                                    marginRight: 4,
                                    marginTop: 0,
                                    marginBottom: 0,
                                    padding: 0,
                                    height: '24px',
                                    fontWeight: 'bold',
                                    paddingLeft: 6,
                                    paddingRight: 6,
                                    borderRadius: 3,
                                    fontSize: "0.75em",
                                    whiteSpace: "nowrap",
                                    maxWidth: 160,
                                    flexShrink: 0
                                }}
                                label={tagName}
                                {...tagProps}
                                size="small"
                            />
                        );
                    });
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        variant="outlined" 
                        placeholder="Tags"
                        style={{ margin: 0 }}
                        InputProps={{
                            ...params.InputProps,
                            style: {
                                ...params.InputProps?.style,
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                height: '32px',
                                alignItems: 'center',
                                display: 'flex',
                                flexWrap: 'nowrap',
                                overflow: 'hidden'
                            }
                        }}
                        inputProps={{
                            ...params.inputProps,
                            style: {
                                padding: '6px 8px',
                                fontSize: '0.875rem',
                                minWidth: 40
                            }
                        }}
                    />
                )}
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap !important",
                        overflow: "hidden",
                        alignItems: "center",
                    },
                    "& .MuiAutocomplete-tag": {
                        margin: "0 4px 0 0",
                        maxWidth: 160,
                    },
                }}
                openOnFocus
                autoHighlight
                disableCloseOnSelect
                clearOnBlur={false}
                componentsProps={{
                    popper: {
                        style: {
                            zIndex: 10004
                        },
                        placement: 'bottom-start',
                        disablePortal: false,
                        modifiers: [
                            {
                                name: 'zIndex',
                                enabled: true,
                                phase: 'write',
                                fn: ({ state }) => {
                                    state.styles.popper.zIndex = 10004;
                                }
                            }
                        ]
                    },
                    paper: {
                        style: {
                            zIndex: 10004,
                            textAlign: 'left',
                            maxHeight: 300,
                            overflow: 'auto',
                            position: 'relative'
                        }
                    }
                }}
                ListboxProps={{
                    style: {
                        maxHeight: 300,
                        overflow: 'auto'
                    }
                }}
            />
            
            {/* Indicador "+X..." renderizado fora do Autocomplete para garantir visibilidade */}
            {shouldShowMoreIndicator && (
                <Chip
                    variant="outlined"
                    style={{
                        backgroundColor: '#e0e0e0',
                        color: "#666",
                        marginLeft: 4,
                        marginTop: 0,
                        marginBottom: 0,
                        padding: 0,
                        height: '24px',
                        fontWeight: 'bold',
                        paddingLeft: 6,
                        paddingRight: 6,
                        borderRadius: 3,
                        fontSize: "0.75em",
                        whiteSpace: "nowrap",
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        display: 'inline-flex',
                        flexShrink: 0,
                        zIndex: 10
                    }}
                    label={`+${remainingTagsCount}...`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowAllTagsModal(true);
                    }}
                    onDelete={undefined}
                    deleteIcon={null}
                    size="small"
                />
            )}
            </div>
            
            {/* Modal para mostrar todas as tags quando houver overflow */}
            {shouldShowMoreIndicator && (
                <Dialog
                    open={showAllTagsModal}
                    onClose={() => setShowAllTagsModal(false)}
                    maxWidth="sm"
                    fullWidth
                    style={{ zIndex: 13050 }}
                    BackdropProps={{
                        style: { zIndex: 13049 }
                    }}
                    PaperProps={{
                        style: { zIndex: 13050 }
                    }}
                >
                    <DialogTitle>Tags ({selecteds?.length || 0})</DialogTitle>
                    <DialogContent>
                        <Box style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '8px',
                            padding: '8px 0'
                        }}>
                            {(selecteds || []).map((option, index) => {
                                const tagName = typeof option === 'string' ? option : (option?.name || '');
                                const tagColor = typeof option === 'string' ? '#eee' : (option?.color || '#eee');
                                
                                const handleRemoveTag = async () => {
                                    const updatedTags = (selecteds || []).filter((tag, idx) => {
                                        if (typeof option === 'string' && typeof tag === 'string') {
                                            return tag !== option;
                                        }
                                        if (typeof option === 'object' && typeof tag === 'object' && option && tag) {
                                            return tag?.id !== option?.id;
                                        }
                                        return idx !== index;
                                    });
                                    
                                    if (isMounted.current) {
                                        setSelecteds(updatedTags);
                                    }
                                    
                                    try {
                                        await syncTags({ contactId: contact.id, tags: updatedTags });
                                    } catch (err) {
                                        console.error('[TagsContainer] Erro ao remover tag:', err);
                                        toastError(err);
                                    }
                                };
                                
                                return (
                                    <Chip
                                        key={index}
                                        variant="outlined"
                                        style={{
                                            backgroundColor: tagColor,
                                            color: "#FFF",
                                            fontWeight: 'bold',
                                            paddingLeft: 8,
                                            paddingRight: 8,
                                            borderRadius: 3,
                                            fontSize: "0.875em"
                                        }}
                                        label={tagName}
                                        onDelete={handleRemoveTag}
                                        size="medium"
                                    />
                                );
                            })}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowAllTagsModal(false)} color="primary">
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Paper>
    )
}
